import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ClipboardCheck, ChevronLeft, CheckCircle2, Clock, FileSearch,
  LogOut, Loader2, ShieldCheck, AlertTriangle, ArrowRight, BookOpen,
  Pencil, Check, X, Sparkles, History, Plus,
} from 'lucide-react';
import CompetitiveLandscapeReview from '@/components/CompetitiveLandscapeReview';
import { useCitationMode, stripCitationMarkers, refPublisher, CITATION_MODES, CitationModeContext } from '@/lib/deepDiveRender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROV_STYLE = {
  ai:             { label: 'AI Draft',      cls: 'bg-slate-100 text-slate-500' },
  human_edited:   { label: 'Human Edited',  cls: 'bg-blue-100 text-blue-700' },
  human_verified: { label: 'Human Verified', cls: 'bg-emerald-100 text-emerald-700' },
};

const STATUS_STYLE = {
  'Not Started':               { cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  'In Review':                 { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: FileSearch },
  'Changes Made':              { cls: 'bg-blue-100 text-blue-800 border-blue-300', icon: ClipboardCheck },
  'Ready for Approval':        { cls: 'bg-violet-100 text-violet-800 border-violet-300', icon: ShieldCheck },
  'Approved for Client Portal':{ cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE['Not Started'];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.cls}`} data-testid="status-chip">
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

const fmtScore = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—');

// Evidence-quality colours per source_type (V2 item 9).
const BADGE_COLOR = {
  green:  'bg-emerald-100 text-emerald-700 border-emerald-200',
  yellow: 'bg-amber-100 text-amber-700 border-amber-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  blue:   'bg-sky-100 text-sky-700 border-sky-200',
  gray:   'bg-slate-100 text-slate-600 border-slate-200',
};
const CHIP_COLOR = {
  green: 'bg-emerald-600', yellow: 'bg-amber-500', orange: 'bg-orange-500',
  blue: 'bg-sky-600', gray: 'bg-slate-500',
};

// Renders text with inline [n] citations turned into clickable, hover-preview
// chips that open the source in a new tab (V2 items 1-3). `refs` is the
// enriched reference list keyed by 1-based number.
function CitedText({ text, refs }) {
  const mode = useCitationMode();
  if (text === null || text === undefined) return null;
  const str = String(text);
  if (mode === CITATION_MODES.BIBLIOGRAPHY) {
    const clean = stripCitationMarkers(str);
    return <span className="whitespace-pre-wrap">{clean}</span>;
  }
  const byN = {};
  (refs || []).forEach((r) => { byN[r.n] = r; });
  const parts = str.split(/(\[\d+\])/g);
  return (
    <span>
      {parts.map((p, i) => {
        const m = p.match(/^\[(\d+)\]$/);
        if (!m) return <span key={i}>{p}</span>;
        const n = Number(m[1]);
        const ref = byN[n];
        const color = ref?.color || 'gray';
        return (
          <span key={i} className="relative inline-block group/cite align-baseline">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('reviewer:jump-ref', { detail: { n } })); }}
              className={`mx-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-bold text-white ${CHIP_COLOR[color]} hover:brightness-110 cursor-pointer align-middle`}
              data-testid={`cite-${n}`}
              title={`Jump to reference [${n}] in the Bibliography`}
            >
              {n}
            </button>
            {ref && (
              <span className="pointer-events-none absolute left-0 bottom-full mb-1 hidden group-hover/cite:block z-50 w-64 rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl text-left">
                <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded border ${BADGE_COLOR[color]}`}>{ref.badge}</span>
                <span className="block text-[11px] font-semibold text-slate-800 mt-1 leading-snug">{ref.title || ref.domain || ref.url}</span>
                <span className="block text-[10px] text-slate-400 mt-0.5">{ref.domain}{ref.year ? ` · ${ref.year}` : ''}</span>
                <span className="block text-[9px] text-indigo-500 mt-1">Click to jump to the Bibliography ↓</span>
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// Deterministic safety net: render ANY section field not shown by an explicit
// EditField above, read-only, with inline [n] citations preserved. This
// guarantees every reference in the library has a visible clickable chip
// somewhere — so numbering can never appear to "skip" (e.g. a ref cited only
// inside company `notable_milestones` used to vanish).
function LeftoverFields({ obj, shown, refs }) {
  const isEmpty = (v) =>
    v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
  const entries = Object.entries(obj || {}).filter(
    ([k, v]) => !shown.includes(k) && !isEmpty(v),
  );
  if (!entries.length) return null;
  return (
    <div className="mt-2 space-y-2 border-t border-dashed border-slate-200 pt-2" data-testid="leftover-fields">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{k.replace(/_/g, ' ')}</div>
          <div className="text-sm text-slate-700 leading-relaxed">
            {Array.isArray(v)
              ? <ul className="list-disc pl-4 space-y-0.5">{v.map((it, i) => (
                  <li key={i}><CitedText text={typeof it === 'string' ? it : JSON.stringify(it)} refs={refs} /></li>
                ))}</ul>
              : <CitedText text={typeof v === 'object' ? JSON.stringify(v) : String(v)} refs={refs} />}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Dashboard tiles ──────────────────────────────────────────────────
function Dashboard({ data, activeStatus, onPick }) {
  if (!data) return null;
  const tiles = (data.status_order || []).map((s) => ({ status: s, count: data.status_counts?.[s] || 0 }));
  return (
    <div className="space-y-3" data-testid="reviewer-dashboard">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Assigned for review · {data.client_name}</div>
          <div className="text-2xl font-black text-slate-900">{data.total_assigned} assets</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-emerald-500 font-semibold">Approved for client</div>
          <div className="text-2xl font-black text-emerald-600" data-testid="approved-count">{data.approved_for_client}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <button
          onClick={() => onPick(null)}
          className={`rounded-lg border p-3 text-left transition-colors ${!activeStatus ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}
          data-testid="tile-all"
        >
          <div className="text-2xl font-black tabular-nums">{data.total_assigned}</div>
          <div className="text-[10px] font-medium mt-0.5">All assets</div>
        </button>
        {tiles.map((t) => {
          const active = activeStatus === t.status;
          const s = STATUS_STYLE[t.status];
          return (
            <button
              key={t.status}
              onClick={() => onPick(active ? null : t.status)}
              className={`rounded-lg border p-3 text-left transition-colors ${active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 bg-white hover:border-slate-400'}`}
              data-testid={`tile-${t.status.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="text-2xl font-black tabular-nums text-slate-900">{t.count}</div>
              <div className={`text-[10px] font-medium mt-0.5 leading-tight ${s?.cls?.split(' ')[1] || 'text-slate-600'}`}>{t.status}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Queue table ──────────────────────────────────────────────────────
function Queue({ rows, onOpen }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="reviewer-queue">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-right px-2 py-2 font-semibold w-10">#</th>
            <th className="text-left px-3 py-2 font-semibold">Asset</th>
            <th className="text-left px-3 py-2 font-semibold">Company</th>
            <th className="text-left px-3 py-2 font-semibold">Indication / TA</th>
            <th className="text-left px-3 py-2 font-semibold">Stage</th>
            <th className="text-right px-3 py-2 font-semibold">Score</th>
            <th className="text-left px-3 py-2 font-semibold">AI rec.</th>
            <th className="text-left px-3 py-2 font-semibold">Status</th>
            <th className="text-left px-3 py-2 font-semibold">Last by</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No assets in this view.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.ea_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`queue-row-${r.ea_id}`}>
              <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-400 text-xs" data-testid={`queue-num-${r.queue_number}`}>{r.queue_number}</td>
              <td className="px-3 py-2 font-semibold text-slate-800">{r.asset_name}</td>
              <td className="px-3 py-2 text-slate-600">{r.biotech_name}</td>
              <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={r.indication}>{r.indication || r.therapeutic_area || '—'}</td>
              <td className="px-3 py-2 text-slate-600">{r.phase || '—'}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700">{fmtScore(r.weighted_avg_score)}</td>
              <td className="px-3 py-2 text-slate-700">
                {r.ai_recommendation}
                {r.tier_change && r.tier_change.from_tier && r.tier_change.from_tier !== r.tier_change.to_tier && (
                  <span
                    className="block mt-0.5 w-fit px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[9px] font-medium cursor-help"
                    title={`AI re-run v${r.tier_change.from_version} → v${r.tier_change.to_version} changed the tier. Open the asset to review the new content.`}
                    data-testid={`queue-tier-change-${r.ea_id}`}
                  >
                    was {r.tier_change.from_tier}
                  </span>
                )}
              </td>
              <td className="px-3 py-2"><StatusChip status={r.review_status} /></td>
              <td className="px-3 py-2 text-[11px] text-slate-500">{r.last_reviewed_by ? `${r.last_reviewed_by.split('@')[0]} · ${fmtDate(r.last_reviewed_at)}` : '—'}</td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => onOpen(r.ea_id, r.queue_number)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
                  data-testid={`open-review-${r.ea_id}`}
                >
                  {r.review_status === 'Not Started' ? 'Start' : 'Continue'} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Deep Dive review panel (editable · Phase 2–5) ────────────────────
function ProvBadge({ prov }) {
  const p = PROV_STYLE[prov || 'ai'];
  return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${p.cls}`} data-testid="prov-badge">{p.label}</span>;
}

// One editable field. `path` is the dotted path (e.g. executive_summary.why_relevant).
// `asArray` joins/splits newline lists (key_strengths etc.).
function EditField({ label, path, value, prov, asArray, onSave, onVerify, locked, refs }) {
  const [editing, setEditing] = useState(false);
  const toText = (v) => Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? x : (x.point || x.text || ''))).join('\n') : (v ?? '');
  const [text, setText] = useState(toText(value));
  useEffect(() => { setText(toText(value)); }, [value]);  // eslint-disable-line
  const display = toText(value);
  if (!display && !editing) return null;

  const save = () => {
    const out = asArray ? text.split('\n').map((s) => s.trim()).filter(Boolean) : text;
    onSave(path, out);
    setEditing(false);
  };
  return (
    <div className="mb-2.5 group" data-testid={`field-${path}`}>
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
        <ProvBadge prov={prov} />
        {!locked && !editing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-indigo-600" title="Edit" data-testid={`edit-${path}`}><Pencil className="w-3 h-3" /></button>
            <button onClick={() => onVerify(path)} className="text-slate-400 hover:text-emerald-600" title="Mark verified" data-testid={`verify-${path}`}><Check className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            rows={asArray ? 4 : Math.min(8, Math.max(2, Math.ceil(text.length / 80)))}
            className="w-full text-sm border border-indigo-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            data-testid={`textarea-${path}`}
          />
          {asArray && <div className="text-[10px] text-slate-400">One item per line</div>}
          <div className="flex gap-2 mt-1">
            <button onClick={save} className="inline-flex items-center gap-1 text-xs bg-indigo-600 text-white rounded px-2 py-1" data-testid={`save-${path}`}><Check className="w-3 h-3" /> Save</button>
            <button onClick={() => { setEditing(false); setText(display); }} className="inline-flex items-center gap-1 text-xs border border-slate-300 rounded px-2 py-1"><X className="w-3 h-3" /> Cancel</button>
          </div>
        </div>
      ) : asArray ? (
        <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-700">{toText(value).split('\n').filter(Boolean).map((x, i) => <li key={i}><CitedText text={x} refs={refs} /></li>)}</ul>
      ) : (
        <div className="text-sm text-slate-700 whitespace-pre-wrap"><CitedText text={display} refs={refs} /></div>
      )}
    </div>
  );
}

function ReviewSection({ title, sectionKey, children, proposed, onResolve, hidden, coverage }) {
  const st = proposed?.status;
  return (
    <div className={`border border-slate-200 rounded-lg p-4 bg-white ${hidden ? 'hidden' : ''}`} data-testid={`section-${(sectionKey || title).replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {coverage !== undefined && coverage !== null && (
          coverage > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`coverage-${sectionKey}`}>
              <CheckCircle2 className="w-3 h-3" /> {coverage} source{coverage === 1 ? '' : 's'} cited
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" title="This evidence section states facts but has no inline citations. Attach a reference (click any field to edit) before approving." data-testid={`coverage-${sectionKey}`}>
              <AlertTriangle className="w-3 h-3" /> No sources cited
            </span>
          )
        )}
      </div>
      {st === 'ready' && (
        <div className="mb-3 border border-violet-300 bg-violet-50 rounded p-2.5 text-xs" data-testid={`proposed-${sectionKey}`}>
          <div className="flex items-center gap-1 font-semibold text-violet-800 mb-1"><Sparkles className="w-3 h-3" /> Proposed revision ready</div>
          <div className="text-slate-600 mb-2">A re-researched version of this section is ready. Accept to replace the current content, or reject to keep yours.</div>
          <div className="flex gap-2">
            <button onClick={() => onResolve(sectionKey, true)} className="text-xs bg-violet-600 text-white rounded px-2 py-1" data-testid={`accept-${sectionKey}`}>Accept revision</button>
            <button onClick={() => onResolve(sectionKey, false)} className="text-xs border border-slate-300 rounded px-2 py-1" data-testid={`reject-${sectionKey}`}>Reject</button>
          </div>
        </div>
      )}
      {st === 'failed' && <div className="mb-2 text-[11px] text-rose-600">Re-research failed — please retry.</div>}
      {children}
    </div>
  );
}

const REC_OPTIONS = ['Strategic Priority', 'High Priority', 'Medium', 'Monitor', 'Low Priority', 'Not Suitable'];

// Count DISTINCT references cited within one section (its inline [n] markers).
// Used to show per-section citation coverage so reviewers can see the
// distribution and manually balance sparse evidence sections before approving.
function sectionCiteCount(obj) {
  const nums = new Set();
  const walk = (v) => {
    if (v === null || v === undefined) return;
    if (typeof v === 'string') {
      const m = v.match(/\[(\d+)\]/g);
      if (m) m.forEach((x) => nums.add(x));
    } else if (Array.isArray(v)) {
      v.forEach(walk);
    } else if (typeof v === 'object') {
      Object.values(v).forEach(walk);
    }
  };
  walk(obj);
  return nums.size;
}

function DeepDive({ ea_id, queueNumber, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  // Tab state must live with the other hooks (before any early return) so hook
  // order is stable. Same tab structure as the Admin deep dive.
  const [tab, setTab] = useState('exec');

  // Jul 2026 · inline [N] chip → jump to the Bibliography tab entry.
  useEffect(() => {
    const onJump = (e) => {
      const n = e?.detail?.n;
      if (!n) return;
      setTab('refs');
      setTimeout(() => {
        const el = document.getElementById(`ref-${n}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-indigo-400', 'rounded');
          setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-400', 'rounded'), 1800);
        }
      }, 120);
    };
    window.addEventListener('reviewer:jump-ref', onJump);
    return () => window.removeEventListener('reviewer:jump-ref', onJump);
  }, []);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/review/asset/${ea_id}`, { withCredentials: true });
    setData(r.data);
  }, [ea_id]);

  useEffect(() => {
    load();
    axios.post(`${API}/review/asset/${ea_id}/start`, {}, { withCredentials: true })
      .then(() => { onChanged?.(); load(); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ea_id]);

  // Poll while any section is re-researching.
  useEffect(() => {
    const pr = data?.proposed_revisions || {};
    if (Object.values(pr).some((p) => p?.status === 'running')) {
      const t = setTimeout(load, 4000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/review/asset/${ea_id}/${path}`, body || {}, { withCredentials: true });
      if (okMsg) toast.success(okMsg);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const saveField = (path, value) => act('field', { path, value }, 'Saved');
  const verifyField = (path) => act('verify-field', { path }, 'Marked verified');
  const reresearch = (section) => act('re-research', { section }, 'Re-research started');
  const resolveRev = (section, accept) => act('re-research/resolve', { section, accept }, accept ? 'Revision accepted' : 'Revision rejected');
  const citation = (body) => act('citation', body, 'Reference updated');

  // Human sign-off — citations are already verified during AI generation.
  const approveReferences = async () => {
    try {
      await axios.post(`${API}/review/asset/${ea_id}/references/approve`, {}, { withCredentials: true });
      toast.success('References approved ✓');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve references');
    }
  };

  const openHistory = async () => {
    const r = await axios.get(`${API}/review/asset/${ea_id}/versions`, { withCredentials: true });
    setHistory(r.data); setShowHistory(true);
  };
  const restore = async (body) => { await act('restore', body, 'Restored'); setShowHistory(false); };

  // Jun 2026 · reset the draft to the latest AI version (discards human
  // edits — old draft snapshotted into History for recovery).
  const resetDraft = async () => {
    if (!window.confirm('Reset this draft to the latest AI version?\n\nAll human edits on this asset will be replaced by the new AI output (the current draft is saved in History). The asset drops back to "In Review".')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/review/asset/${ea_id}/reset-draft`, {}, { withCredentials: true });
      toast.success('Draft reset to latest AI version');
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset draft');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const { summary, draft, review, field_provenance: prov = {}, proposed_revisions: pr = {}, references_enriched: refs = [] } = data;
  const es = draft.executive_summary || {};
  const co = draft.company_intelligence || {};
  const ao = draft.asset_intelligence || {};
  const sci = draft.scientific_assessment || {};
  const cl = draft.competitive_landscape || {};
  const approved = review.review_status === 'Approved for Client Portal';
  const REVIEW_TABS = [
    { key: 'exec',    label: 'Executive Summary' },
    { key: 'scoring', label: 'Scoring' },
    { key: 'company', label: 'Company' },
    { key: 'asset',   label: 'Asset' },
    { key: 'science', label: 'Science' },
    { key: 'comp',    label: 'Competition' },
    { key: 'refs',    label: `Bibliography (${(refs || []).length})` },
  ];
  const F = (label, path, val, arr) => (
    <EditField label={label} path={path} value={val} prov={prov[path]} asArray={arr}
      onSave={saveField} onVerify={verifyField} locked={approved} refs={refs} />
  );

  return (
    <CitationModeContext.Provider value={CITATION_MODES.CITATION}>
    <div className="space-y-4" data-testid="deep-dive">
      <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-queue">
          <ChevronLeft className="w-4 h-4" /> Queue
        </button>
        <div className="flex items-center gap-2">
          <button onClick={openHistory} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 border border-slate-200 rounded px-2 py-1.5" data-testid="history-btn"><History className="w-3.5 h-3.5" /> History</button>
          <StatusChip status={review.review_status} />
          {!approved ? (
            <button onClick={() => act('approve', { comment: '' }, 'Approved — now live on the client portal')} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3 py-1.5 disabled:opacity-50" data-testid="approve-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve for Client Portal
            </button>
          ) : (
            <button onClick={() => act('status', { status: 'In Review' }, 'Unlocked — back in review')} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-1.5" data-testid="unpublish-btn">
              Unlock for Editing
            </button>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-black text-slate-900">{queueNumber ? <span className="text-slate-400 font-bold mr-1.5">#{queueNumber}</span> : null}{summary.asset_name}</h2>
        <div className="text-sm text-slate-500">{summary.biotech_name} · {summary.indication || summary.therapeutic_area} · {summary.phase} · {summary.modality}</div>
        {approved && <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Approved & live on client portal · locked — click "Unlock for Editing" to change</div>}
      </div>

      {data.draft_stale && (
        <div className="border border-violet-300 bg-violet-50 rounded-lg p-3 text-sm text-violet-900 flex items-center justify-between gap-3" data-testid="stale-draft-banner">
          <div className="flex gap-2 items-start">
            <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-violet-600" />
            <div>
              <div className="font-semibold">A newer AI version is available{data.assessment_version ? ` (v${data.assessment_version})` : ''}</div>
              <div className="text-xs text-violet-800 mt-0.5">This draft was seeded from {data.draft_ai_version ? `AI v${data.draft_ai_version}` : 'an older AI run'}. Resetting replaces the draft (including human edits) with the latest AI output — the current draft is saved in History.</div>
            </div>
          </div>
          <button onClick={resetDraft} disabled={busy}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-50"
            data-testid="reset-draft-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Reset draft to latest AI
          </button>
        </div>
      )}

      {data.ai_concern && (() => {
        let c = data.ai_concern;
        if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = { rationale: c }; } }
        const rationale = c.rationale || c.summary || (typeof c === 'string' ? c : '');
        return (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm text-amber-900 flex gap-2" data-testid="ai-concern">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div><div className="font-semibold">AI concern flag{c.claude_recommendation && c.math_recommendation ? ` · scoring says ${c.math_recommendation}, AI judgment says ${c.claude_recommendation}` : ''}</div>{rationale && <div className="mt-0.5">{rationale}</div>}</div>
          </div>
        );
      })()}

      <div className="border-b border-slate-200 flex gap-1 flex-wrap" data-testid="review-tabs">
        {REVIEW_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition-colors ${
              tab === t.key
                ? 'border-indigo-600 text-indigo-700 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`review-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ReviewSection title="Executive Summary" sectionKey="executive_summary" proposed={pr.executive_summary} onResolve={resolveRev} hidden={tab !== 'exec'}>
        {F('Why relevant', 'executive_summary.why_relevant', es.why_relevant)}
        {F('Strategic fit for Viatris', 'executive_summary.strategic_fit_for_viatris', es.strategic_fit_for_viatris)}
        {F('Scientific differentiation', 'executive_summary.scientific_differentiation', es.scientific_differentiation)}
        {F('Commercial opportunity', 'executive_summary.commercial_opportunity', es.commercial_opportunity)}
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div>{F('Key strengths', 'executive_summary.key_strengths', es.key_strengths, true)}</div>
          <div>{F('Key risks', 'executive_summary.key_risks', es.key_risks, true)}</div>
        </div>
        {F('Recommended next action', 'executive_summary.recommended_next_action', es.recommended_next_action)}
        {F('Rationale', 'executive_summary.next_action_rationale', es.next_action_rationale)}
        <LeftoverFields obj={es} refs={refs} shown={['why_relevant','strategic_fit_for_viatris','scientific_differentiation','commercial_opportunity','key_strengths','key_risks','recommended_next_action','next_action_rationale','headline']} />
      </ReviewSection>

      <ReviewSection title="Company Overview" sectionKey="company_intelligence" proposed={pr.company_intelligence} onResolve={resolveRev} hidden={tab !== 'company'} coverage={sectionCiteCount(co)}>
        {F('Overview', 'company_intelligence.company_overview', co.company_overview)}
        <div className="grid sm:grid-cols-2 gap-x-4">
          {F('Headquarters', 'company_intelligence.headquarters', co.headquarters)}
          {F('Founded', 'company_intelligence.founding_year', co.founding_year)}
          {F('Employees', 'company_intelligence.employee_size', co.employee_size)}
          {F('Therapeutic focus', 'company_intelligence.therapeutic_focus', co.therapeutic_focus)}
        </div>
        {F('Pipeline overview', 'company_intelligence.pipeline_overview', co.pipeline_overview)}
        {F('Investors / funding', 'company_intelligence.investors_funding', co.investors_funding)}
        {F('Existing partnerships', 'company_intelligence.existing_partnerships', co.existing_partnerships)}
        {F('Notable milestones', 'company_intelligence.notable_milestones', co.notable_milestones, true)}
        {F('Licensing history', 'company_intelligence.licensing_history', co.licensing_history)}
        <LeftoverFields obj={co} refs={refs} shown={['company_overview','headquarters','founding_year','employee_size','therapeutic_focus','technology_platforms','pipeline_overview','investors_funding','existing_partnerships','notable_milestones','licensing_history']} />
      </ReviewSection>

      <ReviewSection title="Asset Overview" sectionKey="asset_intelligence" proposed={pr.asset_intelligence} onResolve={resolveRev} hidden={tab !== 'asset'} coverage={sectionCiteCount(ao)}>
        <div className="grid sm:grid-cols-2 gap-x-4">
          {F('Target', 'asset_intelligence.target', ao.target)}
          {F('Modality', 'asset_intelligence.modality', ao.modality)}
          {F('Development stage', 'asset_intelligence.development_stage', ao.development_stage)}
          {F('Indications', 'asset_intelligence.indications', Array.isArray(ao.indications) ? ao.indications.join(', ') : ao.indications)}
          {F('Therapeutic area', 'asset_intelligence.therapeutic_area', ao.therapeutic_area)}
          {F('Licensing availability', 'asset_intelligence.licensing_availability', ao.licensing_availability)}
        </div>
        {F('Mechanism of action', 'asset_intelligence.mechanism_of_action', ao.mechanism_of_action)}
        {F('Geographic development', 'asset_intelligence.geographic_development', ao.geographic_development)}
        {F('Regulatory milestones', 'asset_intelligence.regulatory_milestones', ao.regulatory_milestones)}
        {F('Existing partnerships', 'asset_intelligence.existing_partnerships', ao.existing_partnerships)}
        {F('Patent highlights', 'asset_intelligence.patent_highlights', ao.patent_highlights)}
        <LeftoverFields obj={ao} refs={refs} shown={['target','modality','development_stage','indications','therapeutic_area','licensing_availability','mechanism_of_action','geographic_development','regulatory_milestones','existing_partnerships','patent_highlights']} />
      </ReviewSection>

      <ReviewSection title="Science & Clinical Evidence" sectionKey="scientific_assessment" proposed={pr.scientific_assessment} onResolve={resolveRev} hidden={tab !== 'science'} coverage={sectionCiteCount(sci)}>
        {F('Disease overview', 'scientific_assessment.disease_overview', sci.disease_overview)}
        {F('Epidemiology / prevalence', 'scientific_assessment.epidemiology_prevalence', sci.epidemiology_prevalence)}
        {F('Standard of care', 'scientific_assessment.current_standard_of_care', sci.current_standard_of_care)}
        {F('Unmet medical need', 'scientific_assessment.unmet_medical_need', sci.unmet_medical_need)}
        {F('Mechanism rationale', 'scientific_assessment.mechanism_rationale', sci.mechanism_rationale)}
        {F('Key efficacy findings', 'scientific_assessment.key_efficacy_findings', sci.key_efficacy_findings)}
        {F('Key safety findings', 'scientific_assessment.key_safety_findings', sci.key_safety_findings)}
        {F('Differentiation vs competitors', 'scientific_assessment.differentiation_vs_competitors', sci.differentiation_vs_competitors)}
        {F('Advantages', 'scientific_assessment.advantages', sci.advantages)}
        {F('Limitations', 'scientific_assessment.limitations', sci.limitations)}
        {F('Guideline potential', 'scientific_assessment.guideline_potential', sci.guideline_potential)}
        {F('Commercial opportunity', 'scientific_assessment.commercial_opportunity', sci.commercial_opportunity)}
        <LeftoverFields obj={sci} refs={refs} shown={['disease_overview','epidemiology_prevalence','current_standard_of_care','unmet_medical_need','mechanism_rationale','key_efficacy_findings','key_safety_findings','differentiation_vs_competitors','advantages','limitations','guideline_potential','commercial_opportunity']} />
      </ReviewSection>

      <ReviewSection title="Competitive Landscape" sectionKey="competitive_landscape" proposed={pr.competitive_landscape} onResolve={resolveRev} hidden={tab !== 'comp'} coverage={sectionCiteCount(cl)}>
        <CompetitiveLandscapeReview cl={cl} refs={refs} />
      </ReviewSection>

      <ReviewSection title="Scoring" sectionKey="scoring" proposed={pr.scoring} onResolve={resolveRev} hidden={tab !== 'scoring'}>
        <div className="grid sm:grid-cols-2 gap-2">
          {Object.entries(draft.scoring || {}).map(([dim, v]) => (
            <div key={dim} className="border border-slate-100 rounded p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-700 capitalize">{dim.replace(/_/g, ' ')}</span>
                {approved ? (
                  <span className="text-sm font-mono font-bold text-slate-900">{v?.score ?? '—'}</span>
                ) : (
                  <select value={v?.score ?? ''} onChange={(e) => saveField(`scoring.${dim}.score`, Number(e.target.value))}
                    className="text-sm font-mono border border-slate-200 rounded px-1 py-0.5" data-testid={`score-${dim}`}>
                    {['', 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n === '' ? '—' : n}</option>)}
                  </select>
                )}
              </div>
              {F('Rationale', `scoring.${dim}.rationale`, v?.rationale)}
            </div>
          ))}
        </div>
      </ReviewSection>

      <ReviewSection title="Recommendation" sectionKey="recommendation" hidden={tab !== 'exec'}>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1">Priority <ProvBadge prov={prov.recommendation} /></div>
            {approved ? (
              <div className="text-lg font-black text-slate-900">{draft.recommendation}</div>
            ) : (
              <select value={draft.recommendation || ''} onChange={(e) => saveField('recommendation', e.target.value)}
                className="text-base font-bold border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="rec-select">
                {REC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>
          <div><div className="text-[10px] uppercase text-slate-400 font-semibold">Weighted score</div><div className="text-lg font-black text-slate-900">{fmtScore(draft.weighted_avg_score)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-400 font-semibold">Composite</div><div className="text-lg font-black text-slate-900">{draft.composite_score ?? '—'}</div></div>
        </div>
      </ReviewSection>

      <ReviewSection title={`Bibliography (${refs.length || (draft.references || []).length})`} sectionKey="references" hidden={tab !== 'refs'}>
        {data.reference_stats && (data.reference_stats.duplicates_merged > 0 || data.reference_stats.unused_removed > 0) && (
          <div className="mb-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="ref-integrity">
            <CheckCircle2 className="w-3 h-3" /> Bibliography auto-cleaned · sequential 1–{refs.length}
            {data.reference_stats.duplicates_merged ? ` · ${data.reference_stats.duplicates_merged} duplicate(s) merged` : ''}
            {data.reference_stats.unused_removed ? ` · ${data.reference_stats.unused_removed} unused removed` : ''}
          </div>
        )}
        {!approved && (
          <div className="mb-2 text-[11px] text-slate-500">Master bibliography · the complete list of sources supporting this assessment. Sources are verified during AI generation — review the list and approve. Inline citations are consolidated here and hidden from the report body.</div>
        )}
        {data.references_audited && (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold px-3 py-1.5" data-testid="references-audited-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> References approved
            {data.references_audited_at ? ` · ${new Date(data.references_audited_at).toLocaleDateString()}` : ''}
          </div>
        )}
        {!approved && !data.references_audited && (
          <div className="mb-3 flex items-center gap-3 flex-wrap border border-emerald-100 bg-emerald-50/60 rounded-lg p-2.5" data-testid="references-approve-bar">
            <span className="text-[11px] text-slate-600 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              Sources are auto-verified at generation. Confirm the bibliography to sign off.
            </span>
            <button onClick={approveReferences}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5" data-testid="approve-references">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve references
            </button>
          </div>
        )}
        <ol className="space-y-1.5 text-sm">
          {(refs.length ? refs : (draft.references || []).map((r, i) => ({ n: i + 1, url: typeof r === 'string' ? r : (r.url || r.link), title: '', domain: '', badge: 'Web Source', color: 'gray' }))).map((ref) => {
            const i = ref.n - 1;
            const url = ref.url;
            const publisher = refPublisher(ref);
            return (
              <li key={i} className="flex items-start gap-2" data-testid={`ref-${i}`} id={`ref-${ref.n}`}>
                <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded text-[10px] font-bold text-white shrink-0 mt-0.5 ${CHIP_COLOR[ref.color] || CHIP_COLOR.gray}`}>{ref.n}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${BADGE_COLOR[ref.color] || BADGE_COLOR.gray}`}>{ref.badge}</span>
                    {ref.year && <span className="text-[10px] text-slate-400">{ref.year}</span>}
                  </div>
                  <div className="mt-0.5 text-slate-700 leading-snug">
                    {publisher && <span className="font-semibold">{publisher}. </span>}
                    {ref.title
                      ? <span>{ref.title}.</span>
                      : (!publisher && <span className="text-slate-600">Reference</span>)}
                  </div>
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all text-[11px] block mt-0.5">{url}</a>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        {!approved && (
          <button
            onClick={() => { const u = window.prompt('Reference URL'); if (u) { citation({ action: 'add', url: u, title: '' }); } }}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:underline" data-testid="ref-add">
            <Plus className="w-3 h-3" /> Add reference (auto-numbered &amp; badged)
          </button>
        )}
      </ReviewSection>

      {showHistory && history && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowHistory(false)} data-testid="history-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800">Version History</h3>
              <button onClick={() => setShowHistory(false)}><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between border border-slate-200 rounded p-2">
                <div><div className="font-medium text-slate-700">AI original</div><div className="text-[11px] text-slate-400">The untouched AI-generated baseline</div></div>
                <button onClick={() => restore({ ai_original: true })} className="text-xs border border-slate-300 rounded px-2 py-1 hover:bg-slate-50" data-testid="restore-ai">Restore</button>
              </div>
              {(history.history || []).slice().reverse().map((v) => (
                <div key={v.version_id} className="flex items-center justify-between border border-slate-200 rounded p-2">
                  <div><div className="font-medium text-slate-700">{v.label || 'Approved snapshot'}</div><div className="text-[11px] text-slate-400">{v.approved_by} · {fmtDate(v.approved_at)}</div></div>
                  <button onClick={() => restore({ version_id: v.version_id })} className="text-xs border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Restore</button>
                </div>
              ))}
              {(history.history || []).length === 0 && <div className="text-slate-400 text-xs">No approved snapshots yet.</div>}
            </div>
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-400 italic pb-6 flex items-center gap-1">
        <BookOpen className="w-3 h-3" /> Hover any field to Edit or mark Verified. Approve when client-ready — approved assets lock until you click "Unlock for Editing".
      </div>
    </div>
    </CitationModeContext.Provider>
  );
}

// ── Page shell ───────────────────────────────────────────────────────
export default function ReviewerWorkspace() {
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [d, q] = await Promise.all([
        axios.get(`${API}/review/dashboard`, { withCredentials: true }),
        axios.get(`${API}/review/queue${statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : ''}`, { withCredentials: true }),
      ]);
      setDashboard(d.data);
      setRows(q.data.assets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load review workspace');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-400" />
            <div>
              <div className="text-sm font-bold tracking-tight">ForesightRx · Reviewer Workspace</div>
              <div className="text-[10px] text-slate-400">Human-in-the-loop committee review</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-300">{user?.name || user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1 text-slate-300 hover:text-white" data-testid="reviewer-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : selected ? (
          <DeepDive ea_id={selected.ea_id} queueNumber={selected.num} onBack={() => { setSelected(null); refresh(); }} onChanged={refresh} />
        ) : (
          <>
            <Dashboard data={dashboard} activeStatus={statusFilter} onPick={setStatusFilter} />
            <Queue rows={rows} onOpen={(ea_id, num) => setSelected({ ea_id, num })} />
          </>
        )}
      </main>
    </div>
  );
}
